import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Send, Paperclip, X, Mail, User, FileText, MessageSquare, Sparkles, CheckCircle, Briefcase } from 'lucide-react';

const ContactAdmin = () => {
    const { user, isAuthenticated } = useSelector(state => state.auth || {});

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    // Initial animations
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.5, staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setAttachment(e.target.files[0]);
        }
    };

    const removeAttachment = () => {
        setAttachment(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append('subject', subject);
        formData.append('message', message);

        if (!isAuthenticated) {
            formData.append('name', name);
            formData.append('email', email);
            formData.append('senderModel', 'Visitor');
        } else {
            formData.append('senderModel', 'Recruiter');
        }

        if (attachment) {
            formData.append('attachment', attachment);
        }

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Content-Type': 'multipart/form-data' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            await axios.post(`${process.env.REACT_APP_BASE_URL}/contact`, formData, { headers });

            toast.success('Message envoyé avec succès !');
            setSubject('');
            setMessage('');
            if (!isAuthenticated) {
                setName('');
                setEmail('');
            }
            removeAttachment();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || 'Erreur lors de l\'envoi du message');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 font-ubuntu">
            {/* Left Side - Hero / Context */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-yellow-500 via-yellow-600 to-yellow-700 relative overflow-hidden text-white p-12 flex-col justify-center"
            >
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                        className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"
                    />
                </div>

                <div className="relative z-10 max-w-lg">
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                        <Briefcase className="w-5 h-5 text-yellow-200" />
                        <span className="font-medium">Espace Recruteur</span>
                    </div>

                    <h1 className="text-5xl font-bold mb-6 leading-tight">
                        Optimisez vos recrutements
                    </h1>

                    <p className="text-yellow-50 text-xl mb-8 leading-relaxed">
                        Besoin d'aide pour publier une offre ou gérer vos candidats ?
                        Notre équipe support est dédiée aux entreprises.
                    </p>

                    <div className="space-y-4">
                        {[
                            'Assistance prioritaire',
                            'Conseils pour vos offres',
                            'Support technique 7j/7'
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 + idx * 0.1 }}
                                className="flex items-center gap-3"
                            >
                                <div className="p-2 bg-white/20 rounded-full">
                                    <CheckCircle className="w-4 h-4" />
                                </div>
                                <span className="text-lg">{item}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
                >
                    <motion.div variants={itemVariants} className="text-center mb-8">
                        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MessageSquare className="w-8 h-8 text-yellow-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">Contactez-nous</h2>
                        <p className="mt-2 text-gray-500">
                            {isAuthenticated ? 'Une question ? Écrivez-nous.' : 'Vous n\'avez pas de compte Recruteur ?'}
                        </p>
                    </motion.div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {!isAuthenticated && (
                            <>
                                <motion.div variants={itemVariants}>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nom complet</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all outline-none"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Votre nom"
                                        />
                                    </div>
                                </motion.div>

                                <motion.div variants={itemVariants}>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email professionnel</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all outline-none"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="contact@entreprise.com"
                                        />
                                    </div>
                                </motion.div>
                            </>
                        )}

                        <motion.div variants={itemVariants}>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Sujet</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FileText className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    required
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all outline-none"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Objet de la demande"
                                />
                            </div>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
                            <textarea
                                required
                                rows={4}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all outline-none resize-none"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Détaillez votre demande ici..."
                            />
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Pièce jointe</label>
                            <div className="relative">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                    accept="image/*,.pdf,.doc,.docx"
                                />
                                {attachment ? (
                                    <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                                        <div className="flex items-center gap-2 truncate">
                                            <Paperclip className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                                            <span className="text-sm text-yellow-800 truncate">{attachment.name}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={removeAttachment}
                                            className="p-1 hover:bg-yellow-100 rounded-full transition-colors"
                                        >
                                            <X className="h-4 w-4 text-yellow-600" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current.click()}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-yellow-400 hover:text-yellow-600 transition-all group"
                                    >
                                        <Paperclip className="h-5 w-5 group-hover:scale-110 transition-transform" />
                                        <span className="text-sm font-medium">Ajouter un fichier</span>
                                    </button>
                                )}
                            </div>
                        </motion.div>

                        <motion.button
                            variants={itemVariants}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 px-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold rounded-xl shadow-lg shadow-yellow-500/30 flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                            ) : (
                                <>
                                    <span>Envoyer le message</span>
                                    <Send className="w-5 h-5" />
                                </>
                            )}
                        </motion.button>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default ContactAdmin;
